import { useEffect, useState } from "react";
import { useCustom, useCustomMutation } from "@refinedev/core";
import { ToggleField, CheckboxField } from "@fawkes/ui";

import { Drawer } from "./Drawer";

// kp-Epic 7 (kennelplanning-migration) — frontend part 2: the per-user FIELD-SETTINGS drawer. Lets the
// user pick which occupant fields render on the floorplan tiles / pending cards / list rows. Rendered
// inside the page-local <Drawer> (NOT shared @fawkes/ui — the migration is additive). The 32 fields are
// grouped into 5 sections, each a toggle. NAME + KENNEL_NUM are always-on (checked + disabled — the
// server forces them too). Each toggle auto-persists via PUT (optimistic + reconcile from the server's
// resolved set); "Standaard" restores the default set.
//
//   GET kennelplanning/field-preferences  → { visibleFields: string[] }
//   PUT kennelplanning/field-preferences  ← { visibleFields } → resolved { visibleFields }
// Both read/write via Refine against RELATIVE urls (the dataProvider prepends `/api`). The server forces
// NAME + KENNEL_NUM in and drops unknown keys, so we always reconcile state from the PUT response.

/** The translate signature (positional %s/%d via `{ args: [...] }`). */
type Translate = (key: string, options?: { args?: unknown[] }, defaultMessage?: string) => string;

// ── The 32 keys in 5 groups (exact names; rendered in this order) ───────────────────────────────────

export const FIELD_GROUPS: Array<{ groupKey: string; fields: string[] }> = [
  {
    groupKey: "identiteit",
    fields: [
      "NAME",
      "PHOTO",
      "ID",
      "PASPOORT",
      "STAMBOOM",
      "TATOEAGE",
      "SPECIES",
      "RAS",
      "KRUISING",
      "VACHT",
      "KLEUR",
      "KENMERKEN",
      "BIJZONDERHEDEN",
    ],
  },
  {
    groupKey: "status",
    fields: ["GENDER", "CASTRATED", "LEEFTIJD", "GEBOORTE", "PLAATSBAAR", "ZINDELIJK", "DRINKT_FLES"],
  },
  {
    groupKey: "verblijf",
    fields: [
      "DAYS",
      "OPNAME",
      "OPNAME_SOORT",
      "VERTREK_DATUM",
      "VERBLIJF_NUMMER",
      "HALSBAND",
      "GERESERVEERD_TOT",
      "OPMERKING_IN",
    ],
  },
  {
    groupKey: "medisch",
    fields: ["FLAGS", "GEWICHT", "ENTING"],
  },
  {
    groupKey: "locatie",
    fields: ["KENNEL_NUM"],
  },
];

/** All 32 keys, flattened (group order). */
export const ALL_FIELD_KEYS: string[] = FIELD_GROUPS.flatMap((g) => g.fields);

/** Always-on keys — checked + disabled in the UI; the server forces them too. */
export const ALWAYS_ON_FIELDS: ReadonlySet<string> = new Set(["NAME", "KENNEL_NUM"]);

/** The default visible set the "Standaard" reset PUTs (and the page falls back to while loading). */
export const DEFAULT_VISIBLE_FIELDS: string[] = [
  "PHOTO",
  "NAME",
  "KENNEL_NUM",
  "GENDER",
  "DAYS",
  "FLAGS",
];

interface FieldPreferencesResponse {
  visibleFields?: string[] | null;
}

/** Normalize a wire set into a Set, always forcing the always-on keys in. */
function toVisibleSet(fields: string[] | null | undefined): Set<string> {
  const set = new Set(fields ?? []);
  for (const k of ALWAYS_ON_FIELDS) set.add(k);
  return set;
}

export interface FieldSettingsDrawerProps {
  /** Whether the drawer is open. */
  open: boolean;
  /** Close request (Esc / backdrop / close button). */
  onClose: () => void;
  /** Whether the module/fetch is enabled. */
  enabled?: boolean;
  /** Fired whenever the resolved visible set changes so the page can re-render the chips live. */
  onChange: (visibleFields: Set<string>) => void;
  t: Translate;
}

export function FieldSettingsDrawer({
  open,
  onClose,
  enabled = true,
  onChange,
  t,
}: FieldSettingsDrawerProps) {
  // The drawer-local working set. Seeded from GET on open, reconciled from each PUT response.
  const [visible, setVisible] = useState<Set<string>>(() => toVisibleSet(DEFAULT_VISIBLE_FIELDS));
  const [saving, setSaving] = useState(false);

  // Load the current preferences when the drawer opens (and re-load on each open so it's fresh).
  const { data: prefsData, refetch } = useCustom<FieldPreferencesResponse>({
    url: "kennelplanning/field-preferences",
    method: "get",
    queryOptions: { enabled: enabled && open, retry: false },
  });

  useEffect(() => {
    if (open) void refetch();
  }, [open, refetch]);

  useEffect(() => {
    const fields = prefsData?.data?.visibleFields;
    if (fields) {
      const next = toVisibleSet(fields);
      setVisible(next);
      onChange(next);
    }
    // onChange is stable from the parent; reconcile only when the fetched set changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefsData]);

  const { mutate } = useCustomMutation<FieldPreferencesResponse>();

  /** Persist a candidate set: optimistic apply, then reconcile from the server's resolved response. */
  function persist(nextSet: Set<string>) {
    // Always-on keys can never be toggled off.
    for (const k of ALWAYS_ON_FIELDS) nextSet.add(k);
    setVisible(nextSet);
    onChange(nextSet);
    setSaving(true);
    mutate(
      {
        url: "kennelplanning/field-preferences",
        method: "put",
        values: { visibleFields: Array.from(nextSet) },
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: (res) => {
          setSaving(false);
          const resolved = res?.data?.visibleFields;
          if (resolved) {
            const reconciled = toVisibleSet(resolved);
            setVisible(reconciled);
            onChange(reconciled);
          }
        },
        onError: () => {
          // Revert to whatever the server last confirmed by re-reading.
          setSaving(false);
          void refetch();
        },
      },
    );
  }

  function toggleField(key: string, checked: boolean) {
    if (ALWAYS_ON_FIELDS.has(key)) return;
    const next = new Set(visible);
    if (checked) next.add(key);
    else next.delete(key);
    persist(next);
  }

  function resetToDefaults() {
    persist(toVisibleSet(DEFAULT_VISIBLE_FIELDS));
  }

  return (
    <Drawer
      open={open}
      onClose={onClose}
      ariaLabel={t("kennelplanning.fieldSettings.title")}
      closeLabel={t("common.close")}
    >
      <div className="fawkes-kennelplanning__fieldsettings">
        <div className="fawkes-kennelplanning__fieldsettings-header">
          <div className="fawkes-kennelplanning__fieldsettings-heading">
            <h3 className="fawkes-kennelplanning__fieldsettings-title">
              {t("kennelplanning.fieldSettings.title")}
            </h3>
            <span className="fawkes-kennelplanning__fieldsettings-subtitle">
              {t("kennelplanning.fieldSettings.subtitle")}
            </span>
          </div>
          <button
            type="button"
            className="fawkes-kennelplanning__fieldsettings-reset"
            onClick={resetToDefaults}
            disabled={saving}
          >
            {t("kennelplanning.fieldSettings.reset")}
          </button>
        </div>

        <div className="fawkes-kennelplanning__fieldsettings-groups">
          {FIELD_GROUPS.map((group) => (
            <section
              key={group.groupKey}
              className="fawkes-kennelplanning__fieldsettings-group"
            >
              <h4 className="fawkes-kennelplanning__fieldsettings-group-title">
                {t(`kennelplanning.fieldSettings.group.${group.groupKey}`)}
              </h4>
              <div className="fawkes-kennelplanning__fieldsettings-toggles">
                {group.fields.map((key) => {
                  const alwaysOn = ALWAYS_ON_FIELDS.has(key);
                  const checked = alwaysOn || visible.has(key);
                  const label = t(`kennelplanning.fieldSettings.field.${key}`);
                  // Always-on keys render as a disabled checkbox (locked); the rest as toggles.
                  return (
                    <div
                      key={key}
                      className="fawkes-kennelplanning__fieldsettings-row"
                    >
                      {alwaysOn ? (
                        <CheckboxField label={label} checked disabled readOnly />
                      ) : (
                        <ToggleField
                          label={label}
                          checked={checked}
                          disabled={saving}
                          onChange={(e) => toggleField(key, e.target.checked)}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            </section>
          ))}
        </div>
      </div>
    </Drawer>
  );
}
